import { FC, useState } from "react";
import { Abonnement } from "../../lib/interfaces/abonnement.interface";
import { useAllowReabonnement } from "../../lib/hooks/useAbonnements";
import { Link } from "react-router";
import { buttonVariants } from "../ui/button";
import { formatDate } from "../../lib/helpers/date_helpers";
import { Building2, CalendarRange, Check, PlusCircle, Recycle, ShieldCheck } from "lucide-react";
import AugmenterCheptelDialog from "../dialogs/AugmenterCheptelDialog";
import { cn } from "@/lib/utils";

interface Props {
  abonnement: Abonnement | null;
}

const BlocAbonnement: FC<Props> = ({ abonnement: abo }) => {
  const [showAugmenterCheptelDialog, setShowAugmenterCheptelDialog] = useState(false);
  const { data: canReabo } = useAllowReabonnement();
  const isViaStructure = abo?.type === "A" || abo?.type === "N" || abo?.type === "EN";
  const revueLabel = abo?.revue ? "Papier + numérique" : "100 % numérique";

  return (
    <>
      <div className='p-5 sm:p-7 lg:p-8'>
        {/* Abonnement */}
        {!!abo && (
          <div className='grid gap-7 lg:grid-cols-[minmax(0,1fr)_19rem] lg:gap-10'>
            <div className='min-w-0'>
              <div
                className={cn(
                  "mb-4 inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-semibold",
                  abo.actif ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-700"
                )}
              >
                <span className={cn("h-2 w-2 rounded-full", abo.actif ? "bg-emerald-500" : "bg-red-500")} />
                {abo.actif ? "Abonnement actif" : "Abonnement inactif"}
              </div>

              <h2 className='text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl'>{revueLabel}</h2>
              <p className='mt-2 max-w-2xl text-sm leading-6 text-slate-600'>
                Votre accès à L'Abeille de France et aux services associés.
              </p>

              {abo.syndicat && (
                <div className='mt-6 flex items-start gap-3 rounded-xl border border-[#dfd7bd] bg-[#fbfaf6] p-4 text-sm text-slate-700'>
                  <Building2 className='mt-0.5 h-5 w-5 shrink-0 text-[#836917]' />
                  <div>
                    <span className='font-semibold text-slate-900'>Adhésion via votre syndicat</span>
                    <p className='mt-0.5'>{abo.syndicat.nom}</p>
                  </div>
                </div>
              )}

              <dl className='mt-7 grid gap-5 border-t border-slate-100 pt-6 sm:grid-cols-2'>
                <div className='flex gap-3'>
                  <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#f6f1df] text-[#806514]'>
                    <CalendarRange className='h-5 w-5' />
                  </div>
                  <div>
                    <dt className='text-sm text-slate-500'>Période</dt>
                    <dd className='mt-0.5 font-semibold text-slate-900'>
                      {formatDate(abo.dateDebut)} – {formatDate(abo.dateFin)}
                    </dd>
                  </div>
                </div>

                {abo.isV2 && (
                  <div className='flex gap-3'>
                    <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#eef5f1] text-[#255b4b]'>
                      <ShieldCheck className='h-5 w-5' />
                    </div>
                    <div>
                      <dt className='text-sm text-slate-500'>Formule souscrite</dt>
                      <dd className='mt-0.5 font-semibold text-slate-900'>
                        {abo.pack
                          ? `${abo.pack.nom} · ${abo.ruches} ruche${abo.ruches === 1 ? "" : "s"}`
                          : "Aucune formule"}
                      </dd>
                    </div>
                  </div>
                )}

                <div className='flex gap-3'>
                  <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#f6f1df] text-[#806514]'>
                    <Recycle className='h-5 w-5' />
                  </div>
                  <div>
                    <dt className='text-sm text-slate-500'>Éco-contribution</dt>
                    <dd className='mt-0.5 font-semibold text-slate-900'>
                      {abo.citeo ? "Incluse" : "Non incluse"}
                    </dd>
                  </div>
                </div>
              </dl>

              {abo.isV2 &&
                !!abo.pack &&
                abo.actif &&
                abo.type !== "EN" &&
                abo.type !== "A" &&
                abo.type !== "N" &&
                !canReabo && (
                  <button
                    type='button'
                    className='mt-6 inline-flex min-h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:border-[#b99a3c] hover:bg-[#fffaf0] hover:text-slate-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8e7425] focus-visible:ring-offset-2'
                    onClick={() => setShowAugmenterCheptelDialog(true)}
                  >
                    <PlusCircle className='h-4 w-4' /> Augmenter le cheptel assuré
                  </button>
                )}
            </div>

            <aside className='relative overflow-hidden rounded-2xl bg-[#173f35] p-6 text-white shadow-[0_18px_45px_-28px_rgba(23,63,53,0.9)]'>
              <div className='absolute -right-12 -top-12 h-36 w-36 rounded-full border border-[#d7b64d]/30' />
              <div className='absolute -right-4 -top-4 h-20 w-20 rounded-full border border-[#d7b64d]/25' />
              <div className='relative'>
                <div className='flex h-10 w-10 items-center justify-center rounded-full bg-white/10'>
                  <Check className='h-5 w-5 text-[#f4d56f]' />
                </div>
                <p className='mt-8 text-sm font-medium text-emerald-100'>Valable jusqu'au</p>
                <p className='mt-1 text-2xl font-bold tracking-tight'>{formatDate(abo.dateFin)}</p>
                <div className='mt-6 border-t border-white/15 pt-5 text-sm leading-6 text-emerald-50/80'>
                  Début de validité : {formatDate(abo.dateDebut)}
                </div>

                {canReabo === true && !isViaStructure && (
                  <Link
                    to='/sabonner'
                    className={buttonVariants({
                      className:
                        "mt-6 w-full bg-[#efcc5c] text-[#21352e] hover:bg-[#f5d875] focus-visible:ring-[#efcc5c]",
                    })}
                  >
                    Programmer le réabonnement
                  </Link>
                )}
              </div>
            </aside>
          </div>
        )}

        {/* Pas d'abonnement */}
        {!abo && (
          <div className='flex flex-col items-start justify-between gap-6 rounded-2xl bg-[#fbfaf6] p-6 sm:flex-row sm:items-center'>
            <div>
              <h2 className='text-xl font-bold text-slate-950'>Aucun abonnement en cours</h2>
              <p className='mt-2 max-w-xl text-sm leading-6 text-slate-600'>
                Abonnez-vous pour accéder à la revue et aux services réservés aux adhérents.
              </p>
            </div>
            <Link
              to='/sabonner'
              className={buttonVariants({ className: "shrink-0 bg-[#173f35] hover:bg-[#23594a]" })}
            >
              S'abonner
            </Link>
          </div>
        )}
      </div>

      {!!abo && !!abo.pack && (
        <AugmenterCheptelDialog
          abonnement={abo}
          setOpen={setShowAugmenterCheptelDialog}
          open={showAugmenterCheptelDialog}
        />
      )}
    </>
  );
};

export default BlocAbonnement;
